import { useEffect, useId, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { ImageIcon } from 'lucide-react'
import { useBackend } from '../lib/session'
import { burrowAppUrl, publicOrigin } from '../lib/env'
import { collectSources, unsourcedThoughts } from '../lib/blocks'
import { countWords, displayName, longDate, readMinutes, SLUG_RE, slugify } from '../lib/text'
import type { CollectionSummary, Profile, ThoughtLikeBlock, Visibility, Writing } from '../lib/types'
import { Avatar, btn, errorText, labelCls, Switch, useBackdropClose } from './ui'
import { FramedImage } from './FramedImage'

const VISIBILITY: Array<{ value: Visibility; label: string; hint: string }> = [
  { value: 'public', label: 'Public', hint: 'On your profile' },
  { value: 'link', label: 'Link only', hint: 'Not listed' },
  { value: 'followers', label: 'Thinking Along with You', hint: 'Sign in to read' },
]

type SlugState = 'idle' | 'checking' | 'ok' | 'taken' | 'invalid'

/** What an unsourced thought-like block is called in the warning (singular, plural). */
const UNSOURCED_KIND: Record<ThoughtLikeBlock['type'], [string, string]> = {
  thought: ['embedded thought', 'embedded thoughts'],
  margin: ['margin note', 'margin notes'],
  unfold: ['unfolded thought', 'unfolded thoughts'],
}

/** "1 margin note has no source." / "3 embedded thoughts have no source." */
export function unsourcedSummary(list: ThoughtLikeBlock[]): string {
  const n = list.length
  const kinds = new Set(list.map((b) => b.type))
  const [one, many] = UNSOURCED_KIND[kinds.size === 1 ? list[0].type : 'thought']
  return `${n} ${n === 1 ? one : many} ${n === 1 ? 'has' : 'have'} no source.`
}

interface Props {
  writing: Writing
  profile: Profile | null
  onClose: () => void
  onPublished: (w: Writing) => void
  onUnpublished: () => void
  /** Once it's published: open the share card ("Share as image"), as after publishing. */
  onShareImage?: () => void
}

export function PublishSheet({ writing, profile, onClose, onPublished, onUnpublished, onShareImage }: Props) {
  const backend = useBackend()
  const navigate = useNavigate()
  const titleId = useId()
  const [slug, setSlug] = useState(() => writing.slug ?? (slugify(writing.title) || 'untitled'))
  const [slugState, setSlugState] = useState<SlugState>('idle')
  const [visibility, setVisibility] = useState<Visibility>(writing.visibility)
  const [showSources, setShowSources] = useState(writing.show_sources)
  const [collectionId, setCollectionId] = useState<string | null>(writing.collection_id)
  const [shareCollection, setShareCollection] = useState(writing.share_collection)
  const [collections, setCollections] = useState<CollectionSummary[] | null>(null)
  const [picking, setPicking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const slugRef = useRef<HTMLInputElement>(null)
  const backdrop = useBackdropClose(onClose)

  useEffect(() => {
    backend.listCollections().then(setCollections).catch(() => setCollections([]))
  }, [backend])

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    slugRef.current?.focus({ preventScroll: true })
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      previous?.focus?.()
    }
  }, [onClose])

  useEffect(() => {
    if (!SLUG_RE.test(slug)) {
      setSlugState('invalid')
      return
    }
    setSlugState('checking')
    let alive = true
    const t = setTimeout(() => {
      backend
        .isSlugAvailable(slug, writing.id)
        .then((ok) => alive && setSlugState(ok ? 'ok' : 'taken'))
        .catch(() => alive && setSlugState('idle'))
    }, 250)
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [backend, slug, writing.id])

  const published = writing.status === 'published'
  const handle = profile?.handle
  const host = new URL(publicOrigin()).host
  const unsourced = unsourcedThoughts(writing.blocks)
  const sourceCount = collectSources(writing.blocks).length
  const minutes = readMinutes(writing.word_count || countWords(writing.title, writing.blocks))
  const selected = collections?.find((c) => c.id === collectionId) ?? null
  const hasTitle = writing.title.trim() !== ''
  const canPublish = !!handle && hasTitle && slugState !== 'invalid' && slugState !== 'taken' && !busy

  async function publish() {
    setBusy(true)
    setError(null)
    try {
      const w = await backend.publishWriting(writing.id, {
        slug,
        visibility,
        show_sources: showSources,
        collection_id: collectionId,
        share_collection: shareCollection && !!selected?.is_public,
      })
      onPublished(w)
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  async function unpublish() {
    setBusy(true)
    setError(null)
    try {
      await backend.unpublishWriting(writing.id)
      onUnpublished()
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <button type="button" aria-label="Close" onClick={onClose} className="fixed inset-0 bg-foreground/32" />
      <div {...backdrop} className="relative flex min-h-full items-start justify-center px-4 py-6 sm:py-10">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="relative flex w-full max-w-[600px] flex-col gap-5 rounded-2xl bg-card p-6 font-sans shadow-[var(--shadow-sheet)] sm:p-7"
        >
          <div className="flex flex-col gap-1">
            <h2 id={titleId} className="m-0 font-display text-[24px] font-normal text-primary">
              {published ? 'Update this writing' : 'Publish this writing'}
            </h2>
            <p className="m-0 text-[13px]/[1.5] text-muted-foreground">Choose who can read it, check your sources, and see the card people will share.</p>
          </div>

          {published && onShareImage && (
            // Niki, 10/2: the share graphics belong here, where a published writing's settings are.
            <button
              type="button"
              onClick={onShareImage}
              className="flex items-center gap-3 rounded-lg border-[1.5px] border-primary/60 bg-accent/10 px-3.5 py-3 text-left hover:bg-accent/18"
            >
              <ImageIcon aria-hidden className="size-5 shrink-0 text-primary" strokeWidth={1.8} />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-[14px] font-semibold text-primary">Share as image</span>
                <span className="text-[11.5px] text-muted-foreground">A ready-made card of this writing, for posts and stories</span>
              </span>
              <span aria-hidden className="text-[16px] text-primary">
                →
              </span>
            </button>
          )}

          {!handle && (
            <div className="rounded-lg bg-accent/10 px-3 py-2.5 text-[13px] font-medium text-primary">
              Your link is {host}/<em>your-handle</em>/… — add a handle to your Burrow profile first.{' '}
              <a href={`${burrowAppUrl}/settings`} className="font-semibold text-primary underline">
                Open settings
              </a>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label htmlFor="wr-slug" className={labelCls}>
              LINK
            </label>
            <div
              className={`flex items-center overflow-hidden rounded-lg border px-3 text-[13px] ${
                slugState === 'invalid' || slugState === 'taken' ? 'border-primary' : 'border-border/80 focus-within:border-accent'
              }`}
            >
              <span className="shrink-0 py-2.5 text-muted-foreground">
                {host}/{handle ?? '…'}/
              </span>
              <input
                id="wr-slug"
                ref={slugRef}
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/-{2,}/g, '-').slice(0, 80))}
                onBlur={() => setSlug((s) => s.replace(/^-+|-+$/g, '') || 'untitled')}
                spellCheck={false}
                className="min-w-0 flex-1 bg-transparent py-2.5 text-foreground outline-none"
              />
            </div>
            <span aria-live="polite" className="min-h-[1em] text-[11.5px] text-muted-foreground">
              {slugState === 'invalid' && 'Links use lowercase letters, numbers and single dashes.'}
              {slugState === 'taken' && <span className="text-primary">You already have a writing at this link.</span>}
              {slugState === 'checking' && 'Checking…'}
            </span>
          </div>

          <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
            <legend className={`${labelCls} mb-1.5`}>WHO CAN READ</legend>
            <div className="grid grid-cols-1 gap-1.5 text-[13px] font-medium sm:grid-cols-3">
              {VISIBILITY.map((v) => {
                const on = visibility === v.value
                return (
                  <label
                    key={v.value}
                    className={`flex cursor-pointer flex-col gap-0.5 rounded-lg px-3 py-2.5 ${on ? 'border-[1.5px] border-primary bg-accent/18' : 'border border-border/80 hover:bg-foreground/3'}`}
                  >
                    <input type="radio" name="visibility" value={v.value} checked={on} onChange={() => setVisibility(v.value)} className="sr-only" />
                    <span className={`whitespace-nowrap ${on ? 'text-primary' : 'text-foreground'}`}>{v.label}</span>
                    <span className="text-[11px] font-normal text-muted-foreground">{v.hint}</span>
                  </label>
                )
              })}
            </div>
          </fieldset>

          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <span className={labelCls}>PUBLISH DATE</span>
              <div className="rounded-lg border border-border/80 px-3 py-2.5 text-[13px] text-foreground">
                {published && writing.published_at ? `Published · ${longDate(writing.published_at)}` : `Now · ${longDate(new Date())}`}
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className={labelCls}>SOURCES</span>
              <div className="rounded-lg border border-border/80 px-3 py-2 text-[13px] text-foreground">
                <Switch label="Show source lines" checked={showSources} onChange={setShowSources} className="w-full justify-between py-0.5" />
              </div>
            </div>
          </div>

          {showSources && unsourced.length > 0 && (
            <div className="flex items-center gap-2.5 rounded-lg bg-accent/10 px-3 py-2.5 text-[13px] font-medium text-primary">
              <span aria-hidden className="flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px] border-current text-[10px] font-bold">
                !
              </span>
              <span className="flex-1">
                {unsourcedSummary(unsourced)} Readers won’t see where {unsourced.length === 1 ? 'it' : 'they'} came from.
              </span>
              {/* Opens the composer on the first one with its source form open — margin notes, pins and unfolds
                  included — and each Save there moves on to the next, until none are left (useSourceWalk). */}
              <button
                type="button"
                onClick={() => navigate(`/w/${writing.id}?fix=${unsourced[0].id}`)}
                aria-label={
                  unsourced.length === 1
                    ? `Fix: add a source to the ${UNSOURCED_KIND[unsourced[0].type][0]}`
                    : `Fix: add sources to all ${unsourced.length}, one after another`
                }
                className="shrink-0 font-semibold text-primary underline"
              >
                {unsourced.length === 1 ? 'Fix' : 'Fix them'}
              </button>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between">
              <span className={labelCls}>CONNECT A COLLECTION</span>
              <span className="text-[11.5px] text-muted-foreground">Optional</span>
            </div>
            {selected && !picking ? (
              <div className="flex items-center gap-3 rounded-lg border-[1.5px] border-primary bg-accent/12 px-3 py-2.5">
                <Mosaic color={selected.cover_color} />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate font-display text-[14px] text-primary">{selected.name}</span>
                  <span className="text-[11.5px] text-muted-foreground">
                    {selected.thought_count} thought{selected.thought_count === 1 ? '' : 's'}
                    {selected.is_public ? '' : ' · private'}
                  </span>
                </div>
                <button type="button" onClick={() => setPicking(true)} className="text-[13px] font-medium text-primary">
                  Change
                </button>
              </div>
            ) : picking || collections === null ? (
              <div className="flex max-h-56 flex-col gap-1 overflow-y-auto rounded-lg border border-border/80 p-1.5">
                {collections === null && <span className="p-2 text-[13px] text-muted-foreground">Loading collections…</span>}
                {collections?.length === 0 && <span className="p-2 text-[13px] text-muted-foreground">You have no collections yet.</span>}
                {collections && collections.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setCollectionId(null)
                      setPicking(false)
                    }}
                    className="rounded-md px-2.5 py-2 text-left text-[13px] text-muted-foreground hover:bg-foreground/4"
                  >
                    None
                  </button>
                )}
                {collections?.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      setCollectionId(c.id)
                      setPicking(false)
                    }}
                    className={`flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left hover:bg-foreground/4 ${c.id === collectionId ? 'bg-accent/15' : ''}`}
                  >
                    <Mosaic color={c.cover_color} size={28} />
                    <span className="min-w-0 flex-1 truncate font-display text-[14px] text-foreground">{c.name}</span>
                    <span className="text-[11px] text-muted-foreground">{c.is_public ? `${c.thought_count}` : 'Private'}</span>
                  </button>
                ))}
              </div>
            ) : (
              <button type="button" onClick={() => setPicking(true)} className="rounded-lg border border-dashed border-border px-3 py-2.5 text-left text-[13px] text-muted-foreground hover:bg-foreground/3">
                Choose the collection behind this writing
              </button>
            )}
            {selected && (
              <label className={`flex items-center justify-between gap-3 px-1 py-1.5 text-[13px] ${selected.is_public ? 'text-foreground/80' : 'text-muted-foreground/70'}`}>
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    disabled={!selected.is_public}
                    checked={shareCollection && selected.is_public}
                    onChange={(e) => setShareCollection(e.target.checked)}
                    className="accent-wine"
                  />
                  Share the collection with readers
                </span>
                <span className="text-right text-[11.5px] text-muted-foreground">{selected.is_public ? 'Appears after the sources' : 'Private collections stay private'}</span>
              </label>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className={labelCls}>LINK PREVIEW</span>
            <div className="overflow-hidden rounded-2xl bg-background">
              <div className="relative h-[150px] bg-muted">
                {writing.cover_url && (
                  <FramedImage src={writing.cover_url} frame={writing.cover_frame} className="absolute inset-0">
                    <span aria-hidden className="absolute inset-0 bg-[linear-gradient(to_top,rgba(45,27,30,.55),transparent_60%)]" />
                  </FramedImage>
                )}
                <div className="absolute inset-x-4 bottom-3.5 flex flex-col gap-1">
                  <span className="line-clamp-2 font-display text-[22px]/[1.1] text-on-scrim">{writing.title || 'Untitled'}</span>
                  {writing.subtitle && <span className="line-clamp-1 font-serif text-[13px] font-light italic text-on-scrim/85">{writing.subtitle}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 px-3.5 py-2.5 text-[11.5px] font-medium text-muted-foreground">
                <Avatar src={profile?.avatar_url} name={displayName(profile)} size={18} />
                {displayName(profile)} · {minutes} min read{sourceCount > 0 && showSources ? ` · ${sourceCount} sources` : ''}
                <span className="ml-auto text-muted-foreground">{host}</span>
              </div>
            </div>
          </div>

          {!hasTitle && <p className="m-0 text-[13px] font-medium text-primary">Give it a title before publishing.</p>}
          {error && (
            <p role="alert" className="m-0 text-[13px] font-medium text-primary">
              {error}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-end gap-2.5">
            {published && (
              <button type="button" onClick={unpublish} disabled={busy} className="mr-auto text-[13px] font-medium text-muted-foreground hover:text-primary">
                Unpublish
              </button>
            )}
            <button type="button" onClick={onClose} className={`${btn.outlineLg}`}>
              {published ? 'Close' : 'Keep as draft'}
            </button>
            <button type="button" onClick={publish} disabled={!canPublish} className={`${btn.primaryLg}`}>
              {busy ? 'Publishing…' : published ? 'Update' : 'Publish now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/** A collection's cover colour, as the app's collection tiles show it. */
function Mosaic({ color, size = 40 }: { color: string | null; size?: number }) {
  return <span aria-hidden style={{ width: size, height: size, background: color || 'var(--accent)' }} className="shrink-0 rounded-lg" />
}
